import * as React from "react";
import {useTheme} from "@mui/material/styles";
import {useNavigate, useLocation} from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import {
  AppBar,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Grid
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import properties from "constants/ApplicationConstants";
import {POST, GET, PATCH} from "APIclient";

export default function AddJobVacancy() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [openingId, setOpeningId] = React.useState(null);

  const [formData, setFormData] = React.useState({
    platform: "",
    positionName: "",
    positionType: "",
    minExp: "",
    maxExp: "",
    noOfOpenings: "",
    jobType: "",
    jobLocation: "",
    skills: ""
  });

  const [errors, setErrors] = React.useState({
    platform: "",
    positionName: "",
    positionType: "",
    noOfOpenings: "",
    jobType: "",
    jobLocation: "",
    skills: ""
  });

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [snackColor, setSnackColor] = React.useState("success");
  const [snackMessage, setSnackMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const validateField = (name, value) => {
    if (!value || value.toString().trim() === "") {
      return `${name.replace(/([A-Z])/g, " $1").trim()} is required.`;
    }
    if (name === "noOfOpenings" && value) {
      if (isNaN(value) || Number(value) <= 0) {
        return "Number of openings must be a positive number.";
      }
    }
    if (name === "maxExp" && value && formData.minExp && Number(value) < Number(formData.minExp)) {
      return "Maximum experience cannot be less than minimum experience.";
    }
    return "";
  };

  const isSaveDisabled =
    Object.values(errors).some(error => error !== "") ||
    Object.values(formData).some(value => !value || value.toString().trim() === "");

  const handleChange = e => {
    const {name, value} = e.target;

    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    const validationError = validateField(name, value);
    setErrors(prevState => ({
      ...prevState,
      [name]: validationError
    }));
  };

  const handleClose = () => {
    setSnackOpen(false);
  };

  const fetchJobData = async id => {
    try {
      setLoading(true);
      const response = await GET(`admin/job-opening/${id}`);
      if (response?.status === 200) {
        const data = response?.data?.data;
        setFormData({
          platform: data.platform || "",
          positionName: data.positionName || "",
          positionType: data.positionType || "",
          minExp: data.minExp || "",
          maxExp: data.maxExp || "",
          noOfOpenings: data.noOfOpenings || "",
          jobType: data.jobType || "",
          jobLocation: data.jobLocation || "",
          skills: data.skills || ""
        });
      } else {
        setSnackColor("error");
        setSnackMessage("Failed to fetch job data.");
        setSnackOpen(true);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Error fetching job data.";
      setSnackColor("error");
      setSnackMessage(errorMessage);
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("openingId");
    if (id) {
      setIsEditMode(true);
      setOpeningId(id);
      fetchJobData(id);
    }
  }, [location]);

  const handleSubmit = async () => {
    const validationErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) validationErrors[key] = error;
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      platform: formData.platform,
      positionName: formData.positionName,
      positionType: formData.positionType,
      minExp: Number(formData.minExp),
      maxExp: Number(formData.maxExp),
      noOfOpenings: formData.noOfOpenings,
      jobType: formData.jobType,
      jobLocation: formData.jobLocation,
      skills: formData.skills
    };

    try {
      setLoading(true);
      if (isEditMode) {
        const url = `admin/update-job-opening/${openingId}`;
        const response = await PATCH(url, payload);
        if (response?.status === 200) {
          setSnackColor("success");
          setSnackMessage("Job vacancy updated successfully.");
          setSnackOpen(true);
          setFormData({
            platform: "",
            positionName: "",
            positionType: "",
            minExp: "",
            maxExp: "",
            noOfOpenings: "",
            jobType: "",
            jobLocation: "",
            skills: ""
          });
          setTimeout(() => {
            navigate("/JobVacancylist");
          }, 2000);
        } else {
          throw new Error(response?.data?.message || "Failed to update job vacancy.");
        }
      } else {
        const response = await POST("admin/add-job-opening", payload);
        if (response?.status === 200) {
          setSnackColor("success");
          setSnackMessage("Job vacancy added successfully.");
          setSnackOpen(true);
          setFormData({
            platform: "",
            positionName: "",
            positionType: "",
            minExp: "",
            maxExp: "",
            noOfOpenings: "",
            jobType: "",
            jobLocation: "",
            skills: ""
          });
          setTimeout(() => {
            navigate("/JobVacancylist");
          }, 2000);
        } else {
          throw new Error(response?.data?.message || "Failed to add job vacancy.");
        }
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        `An error occurred while ${isEditMode ? "updating" : "adding"} the job vacancy.`;
      setSnackColor("error");
      setSnackMessage(errorMessage);
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox container m={2}>
        <AppBar position="static">
          <Card>
            <MDBox
              pt={3}
              mb={0}
              pb={4}
              px={4}
              sx={{borderRadius: "5px", border: "1px solid #D3D3D3"}}
            >
              <InputLabel
                sx={{fontWeight: "bold", fontFamily: properties.fontFamily}}
                id="jobVacancy"
              >
                {isEditMode ? "Update Job Opening" : "Add Job Openings"}
              </InputLabel>

              <Grid container spacing={2} mt={2}>
                <Grid item xs={12} sm={4} mt={1}>
                  <FormControl fullWidth variant="standard" error={!!errors.platform}>
                    <InputLabel>
                      Job Platform <span style={{color: "red"}}>*</span>
                    </InputLabel>
                    <Select
                      name="platform"
                      value={formData.platform}
                      onChange={handleChange}
                      label="Job Platform"
                      disabled={loading}
                    >
                      <MenuItem value="" disabled>
                        Select a platform
                      </MenuItem>
                      <MenuItem value="QA">QA</MenuItem>
                      <MenuItem value="Java Developer">Java Developer</MenuItem>
                      <MenuItem value="Admin/HR - 1">Admin/HR - 1</MenuItem>
                      <MenuItem value="Android Developer">Android Developer</MenuItem>
                      <MenuItem value="Salesforce Developer">Salesforce Developer</MenuItem>
                      <MenuItem value="React Developer">React Developer</MenuItem>
                      <MenuItem value="Angular Developer">Angular Developer</MenuItem>
                      <MenuItem value="PHP Developer">PHP Developer</MenuItem>
                      <MenuItem value="Python Developer">Python Developer</MenuItem>
                      <MenuItem value="AP001-01">AP001-01</MenuItem>
                    </Select>
                    {errors.platform && (
                      <MDTypography variant="caption" color="error">
                        {errors.platform}
                      </MDTypography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDInput
                    fullWidth
                    name="positionName"
                    value={formData.positionName}
                    type="text"
                    label={
                      <>
                        Position Name <span style={{color: properties.colors.Red}}>*</span>
                      </>
                    }
                    variant="standard"
                    onChange={handleChange}
                    helperText={errors.positionName}
                    error={!!errors.positionName}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={4} mt={1}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel id="positionType">
                      Position Type <span style={{color: properties.colors.Red}}>*</span>
                    </InputLabel>
                    <Select
                      name="positionType"
                      value={formData.positionType}
                      onChange={handleChange}
                      IconComponent={ExpandMoreIcon}
                      sx={{
                        "& .MuiSelect-select": {color: "#000"}
                      }}
                      disabled={loading}
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      <MenuItem value="experience">Experience</MenuItem>
                      <MenuItem value="junior">Junior</MenuItem>
                      <MenuItem value="mid-level">Mid-Level</MenuItem>
                      <MenuItem value="senior">Senior</MenuItem>
                      <MenuItem value="lead">Lead</MenuItem>
                      <MenuItem value="manager">Manager</MenuItem>
                    </Select>
                    {errors.positionType && (
                      <MDTypography variant="caption" color="error">
                        {errors.positionType}
                      </MDTypography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4} mt={1}>
                  <FormControl fullWidth variant="standard">
                    <InputLabel id="jobType">
                      Job Type <span style={{color: properties.colors.Red}}>*</span>
                    </InputLabel>
                    <Select
                      name="jobType"
                      value={formData.jobType}
                      onChange={handleChange}
                      IconComponent={ExpandMoreIcon}
                      sx={{
                        "& .MuiSelect-select": {color: "#000"}
                      }}
                      disabled={loading}
                    >
                      <MenuItem value="">
                        <em>Select</em>
                      </MenuItem>
                      <MenuItem value="Full-time">Full-time</MenuItem>
                      <MenuItem value="Part-time">Part-time</MenuItem>
                      <MenuItem value="Contract">Contract</MenuItem>
                      <MenuItem value="Freelance">Freelance</MenuItem>
                    </Select>
                    {errors.jobType && (
                      <MDTypography variant="caption" color="error">
                        {errors.jobType}
                      </MDTypography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDInput
                    fullWidth
                    name="minExp"
                    value={formData.minExp}
                    type="number"
                    label={<>Min Experience (Years)</>}
                    variant="standard"
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDInput
                    fullWidth
                    name="maxExp"
                    value={formData.maxExp}
                    type="number"
                    label={<>Max Experience (Years)</>}
                    variant="standard"
                    onChange={handleChange}
                    helperText={errors.maxExp}
                    error={!!errors.maxExp}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <MDInput
                    fullWidth
                    name="noOfOpenings"
                    value={formData.noOfOpenings}
                    type="number"
                    label={
                      <>
                        Number of Openings <span style={{color: properties.colors.Red}}>*</span>
                      </>
                    }
                    variant="standard"
                    onChange={handleChange}
                    helperText={errors.noOfOpenings}
                    error={!!errors.noOfOpenings}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDInput
                    fullWidth
                    name="jobLocation"
                    value={formData.jobLocation}
                    type="text"
                    label={
                      <>
                        Job Location <span style={{color: properties.colors.Red}}>*</span>
                      </>
                    }
                    variant="standard"
                    onChange={handleChange}
                    helperText={errors.jobLocation}
                    error={!!errors.jobLocation}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDInput
                    fullWidth
                    multiline
                    maxRows={4}
                    name="skills"
                    value={formData.skills}
                    type="text"
                    label={
                      <>
                        Required Skills <span style={{color: properties.colors.Red}}>*</span>
                      </>
                    }
                    variant="standard"
                    onChange={handleChange}
                    helperText={
                      errors.skills || "Enter skills separated by commas (e.g., Java, Spring Boot)"
                    }
                    error={!!errors.skills}
                    disabled={loading}
                  />
                </Grid>
              </Grid>

              <MDBox mt={3} mb={2} sx={{display: "flex", justifyContent: "flex-end"}}>
                <MDBox sx={{display: "flex", gap: 2}}>
                  <MDButton
                    variant="gradient"
                    color="warning"
                    sx={{width: "20ch"}}
                    onClick={() => navigate(-1)}
                    disabled={loading}
                  >
                    Back
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    sx={{
                      marginRight: "10px",
                      width: "20ch",
                      fontFamily: properties.fontFamily,
                      background: properties.colors.bgcolorbluethem
                    }}
                    onClick={handleSubmit}
                    disabled={isSaveDisabled || loading}
                  >
                    {isEditMode ? "Update" : "Add"}
                  </MDButton>
                </MDBox>
              </MDBox>
              <Snackbar
                open={snackOpen}
                anchorOrigin={{vertical: "top", horizontal: "right"}}
                autoHideDuration={6000}
                onClose={handleClose}
              >
                <Alert
                  onClose={handleClose}
                  severity={snackColor === "success" ? "success" : "error"}
                  variant="filled"
                  sx={{width: "100%", color: "#fff"}}
                >
                  {snackMessage}
                </Alert>
              </Snackbar>
            </MDBox>
          </Card>
        </AppBar>
      </MDBox>
    </DashboardLayout>
  );
}
